import { NotificationService } from 'src/app/shared/services/notification.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { inject, Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { SelectItem, ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { HelperService } from './../../../_services/helper.service';

import { ActivityServices } from '../services/activities.services';
import { BaseServices } from './../../kuba.services';
import { environment } from 'src/environments/environment';
import { ProgressLoaderConfig } from '../../shared/components/progress-loader/progress-loader.component';

interface BusinessItem {
  id: number;
  companyName?: string;
  name?: string;
  CompanyName?: string;
  hasChecklist?: boolean;
  hasResponsiblePeople?: boolean;
  hasResponsibleExecution?: boolean;
  hasResponsibleFollowUp?: boolean;
  disabled?: boolean;
}

interface ChecklistItem {
  id: number;
  title: string;
}

@Component({
  selector: 'portal-activity-new',
  templateUrl: 'portal-activity-new.component.html',
  styleUrls: ['portal-activity-new.component.scss'],
  standalone: false
})
export class PortalActivityNewComponent implements OnInit {
  private readonly notifications = inject(NotificationService);
  @ViewChild('progressLoader', { static: false }) progressLoader: any;

  // Form groups for each column
  activityInfoForm: FormGroup;
  notificationForm: FormGroup;
  assignmentForm: FormGroup;

  // Activity Information
  repetitionOptions: SelectItem[] = [];
  checklistOptions: SelectItem[] = [];
  uploadedFiles: File[] = [];
  filePreviewUrls: string[] = [];

  // Loading state
  isLoading: boolean = false;
  isSavingActivities: boolean = false;

  // Progress loader configuration
  progressLoaderConfig: ProgressLoaderConfig = {
    isVisible: false,
    title: 'CREATING_ACTIVITIES',
    message: 'DONT_CLOSE_PAGE',
    progress: 0,
    totalItems: 0,
    estimatedTimeRemaining: 0,
    initialEstimatedTime: 0,
    showProgressBar: true,
    showTimeRemaining: true,
    showItemCount: true
  };

  // Notification Settings
  beforeDeadlineOptions: SelectItem[] = [];
  afterDeadlineOptions: SelectItem[] = [];

  // Assignment
  businessList: BusinessItem[] = [];
  selectedBusinesses: BusinessItem[] = [];
  businessSearchTerm: string = '';
  businessTableLoading: boolean = false;
  businessTablePageSize: number = 10;
  businessTableCurrentPage: number = 0;
  businessTableData: BusinessItem[] = [];
  private businessSearchTimeout: any;
  filteredBusinessList: BusinessItem[] = [];
  selectAllChecked: boolean = false;
  minDate!: Date;
  private subscriptions: Subscription[] = [];

  constructor(
    private activityServices: ActivityServices,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    protected location: Location,
    private translate: TranslateService,
    private confirmationService: ConfirmationService
  ) {
    this.initializeForms();
  }

  ngOnInit() {
    document.querySelector('body').classList.remove('opened');
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);
    this.loadInitialData();
  }

  private initializeForms() {
    // Single form for all fields
    this.activityInfoForm = this.formBuilder.group({
      title: ['', Validators.required],
      description: [''],
      repetitionId: [1], // Default to "No repetition"
      checklistId: [''],
      attachments: [[]],
      deadline: [''],
      beforeDeadline: [0],
      afterDeadline: [0]
    });

    // Keep separate forms for compatibility but don't use them for validation
    this.notificationForm = this.formBuilder.group({
      deadline: [''],
      beforeDeadline: [0],
      afterDeadline: [0]
    });

    this.assignmentForm = this.formBuilder.group({
      selectedBusinesses: [[], Validators.required]
    });
  }

  private loadInitialData() {
    this.loadRepetitionOptions();
    this.loadChecklists();
    this.loadNotificationOptions();
    this.loadBusinesses();
  }

  private loadRepetitionOptions() {
    this.subscriptions.push(
      this.translate.stream('REPETITION_LIST').subscribe(val => {
        this.repetitionOptions = [
          { label: val.REPETITION_LIST_NO_REPETITION, value: 1 },
          { label: val.REPETITION_LIST_DAILY, value: 2 },
          { label: val.REPETITION_LIST_WEEKDAYS, value: 3 },
          { label: val.REPETITION_LIST_EVERY_WEEK, value: 4 },
          { label: val.REPETITION_LIST_EVERY_2_WEEKS, value: 5 },
          { label: val.REPETITION_LIST_EVERY_3_WEEKS, value: 6 },
          { label: val.REPETITION_LIST_EVERY_6_WEEKS, value: 22 },
          { label: val.REPETITION_LIST_EVERY_MONTH, value: 7 },
          { label: val.REPETITION_LIST_EVERY_2_MONTH, value: 8 },
          { label: val.REPETITION_LIST_EVERY_3_MONTH, value: 9 },
          { label: val.REPETITION_LIST_EVERY_4_MONTH, value: 10 },
          { label: val.REPETITION_LIST_EVERY_6_MONTH, value: 11 },
          { label: val.REPETITION_LIST_EVERY_YEAR, value: 12 },
          { label: val.REPETITION_LIST_EVERY_2_YEARS, value: 13 },
          { label: val.REPETITION_LIST_EVERY_3_YEARS, value: 14 },
          { label: val.REPETITION_LIST_EVERY_4_YEARS, value: 15 },
          { label: val.REPETITION_LIST_EVERY_5_YEARS, value: 16 },
          { label: val.REPETITION_LIST_EVERY_6_YEARS, value: 17 },
          { label: val.REPETITION_LIST_EVERY_7_YEARS, value: 18 },
          { label: val.REPETITION_LIST_EVERY_8_YEARS, value: 19 },
          { label: val.REPETITION_LIST_EVERY_9_YEARS, value: 20 },
          { label: val.REPETITION_LIST_EVERY_10_YEARS, value: 21 }
        ];
      })
    );
  }

  private loadChecklists() {
    const portalId = BaseServices.PortalId;

    this.subscriptions.push(
      this.activityServices.getChecklistsByPortal(portalId).subscribe({
        next: response => {
          // Transform response to SelectItem format
          this.checklistOptions = [
            { label: this.translate.instant('SELECT'), value: null },
            ...(response || []).map((checklist: any) => ({
              label:
                checklist.name ||
                checklist.title ||
                checklist.Name ||
                checklist.Title,
              value: checklist.id || checklist.Id
            }))
          ];
        },
        error: error => {
          console.error('Error loading checklists:', error);
          // Fallback to empty options
          this.checklistOptions = [
            { label: this.translate.instant('SELECT'), value: null }
          ];
          
        }
      })
    );
  }

  private loadNotificationOptions() {
    // Before deadline options (0-14 days)
    this.beforeDeadlineOptions = [];
    for (let i = 0; i <= 14; i++) {
      if (i === 0) {
        this.beforeDeadlineOptions.push({
          label: this.translate.instant('ON_DEADLINE_DATE'),
          value: i
        });
      } else {
        this.beforeDeadlineOptions.push({
          label: i.toString(),
          value: i
        });
      }
    }

    // After deadline options
    this.afterDeadlineOptions = [
      { label: this.translate.instant('ON_DEADLINE_DATE'), value: 0 },
      { label: '1', value: 1 },
      { label: '2', value: 2 },
      { label: '3', value: 3 },
      { label: '7', value: 7 },
      { label: this.translate.instant('TREE_WEEKS'), value: 21 },
      { label: this.translate.instant('ONE_MONTH'), value: 30 }
    ];
  }

  private loadBusinesses() {
    this.businessTableLoading = true;
    const portalId = BaseServices.PortalId; // Use PortalId for portal users

    this.subscriptions.push(
      this.activityServices
        .getBusinessesForPortalActivities(portalId)
        .subscribe({
          next: response => {
            // Ensure response is an array and transform data
            const rawBusinesses = Array.isArray(response)
              ? response
              : response?.Businesses || [];
            this.businessList = rawBusinesses.map((business: any) => ({
              id: business.BusinessId,
              name: business.Name,
              companyName: business.Name,
              hasChecklist: business.HasChecklist,
              hasResponsiblePeople:
                business.HasResponsibleForExecution &&
                business.HasResponsibleForFollowUp,
              hasResponsibleExecution: business.HasResponsibleForExecution,
              hasResponsibleFollowUp: business.HasResponsibleForFollowUp,
              disabled: false
            }));

            // Apply search filter and sort alphabetically
            this.filteredBusinessList = this.applyBusinessSearchFilter(
              this.businessList
            );
            this.updateBusinessTableData();

            this.businessTableLoading = false;
          },
          error: error => {
            console.error('Error loading businesses:', error);
            this.businessTableLoading = false;
            
          }
        })
    );
  }

  onChecklistChange() {
    const checklistId = this.activityInfoForm.get('checklistId')?.value;

    // Clear selected businesses when checklist changes
    this.selectedBusinesses = [];
    this.selectAllChecked = false;
    this.updateBusinessTableData();

    if (checklistId) {
      this.loadBusinessesWithChecklist(checklistId);
    } else {
      this.loadBusinesses();
    }
  }

  private loadBusinessesWithChecklist(checklistId: number) {
    this.businessTableLoading = true;
    const portalId = BaseServices.PortalId;

    this.subscriptions.push(
      this.activityServices
        .getBusinessesForPortalActivities(portalId, checklistId)
        .subscribe({
          next: response => {
            // Ensure response is an array and transform data
            const rawBusinesses = Array.isArray(response)
              ? response
              : response?.Businesses || [];
            this.businessList = rawBusinesses.map((business: any) => ({
              id: business.BusinessId,
              name: business.Name,
              companyName: business.Name,
              hasChecklist: business.HasChecklist,
              hasResponsiblePeople:
                business.HasResponsibleForExecution &&
                business.HasResponsibleForFollowUp,
              hasResponsibleExecution: business.HasResponsibleForExecution,
              hasResponsibleFollowUp: business.HasResponsibleForFollowUp,
              disabled: false
            }));

            // Apply search filter and sort alphabetically
            this.filteredBusinessList = this.applyBusinessSearchFilter(
              this.businessList
            );
            this.updateBusinessTableData();

            this.businessTableLoading = false;
          },
          error: error => {
            console.error('Error loading businesses with checklist:', error);
            this.businessTableLoading = false;
            
          }
        })
    );
  }

  onBusinessSearch(event: any) {
    this.businessSearchTerm = event.target.value;

    // Clear previous timeout
    if (this.businessSearchTimeout) {
      clearTimeout(this.businessSearchTimeout);
    }

    // Set new timeout for debounced search
    this.businessSearchTimeout = setTimeout(() => {
      this.businessTableCurrentPage = 0; // Reset to first page
      this.filteredBusinessList = this.applyBusinessSearchFilter(
        this.businessList
      );
      this.updateBusinessTableData();
    }, 300); // 300ms delay
  }

  onBusinessSelectionChange() {
    this.assignmentForm.patchValue({
      selectedBusinesses: this.selectedBusinesses
    });
    this.updateSelectAllState();
  }

  /**
   * Check if a business is selected
   */
  isBusinessSelected(business: BusinessItem): boolean {
    return this.selectedBusinesses.some(
      selected => selected.id === business.id
    );
  }

  /**
   * Handle individual business checkbox change
   */
  onBusinessCheckboxChange(business: BusinessItem, event: any) {
    if (event.checked) {
      // Add to selection if not already selected
      if (!this.isBusinessSelected(business)) {
        this.selectedBusinesses.push(business);
      }
    } else {
      // Remove from selection
      this.selectedBusinesses = this.selectedBusinesses.filter(
        selected => selected.id !== business.id
      );
    }

    this.onBusinessSelectionChange();
  }

  /**
   * Handle select all available businesses
   */
  onSelectAllChange() {
    if (this.selectAllChecked) {
      // Deselect all
      this.selectedBusinesses = [];
    } else {
      // Select all available businesses from current page
      const availableBusinesses = this.businessTableData.filter(
        business => !this.isBusinessDisabled(business)
      );

      // Add available businesses to selected list (avoid duplicates)
      availableBusinesses.forEach(business => {
        if (
          !this.selectedBusinesses.find(selected => selected.id === business.id)
        ) {
          this.selectedBusinesses.push(business);
        }
      });
    }

    this.onBusinessSelectionChange();
  }

  /**
   * Toggle select all available businesses (button click)
   */
  toggleSelectAllBusinesses() {
    if (this.isAllBusinessesSelected()) {
      // Deselect all
      this.selectedBusinesses = [];
    } else {
      // Select all available businesses from filtered list
      const availableBusinesses = this.filteredBusinessList.filter(
        business => !this.isBusinessDisabled(business)
      );
      this.selectedBusinesses = [...availableBusinesses];
    }

    // Update the select all checkbox state
    this.updateSelectAllState();
    this.onBusinessSelectionChange();
  }

  /**
   * Check if all available businesses are selected
   */
  isAllBusinessesSelected(): boolean {
    const availableBusinesses = this.filteredBusinessList.filter(
      business => !this.isBusinessDisabled(business)
    );
    return (
      availableBusinesses.length > 0 &&
      availableBusinesses.every(business =>
        this.selectedBusinesses.some(selected => selected.id === business.id)
      )
    );
  }

  /**
   * Update select all checkbox state
   */
  private updateSelectAllState() {
    const availableBusinesses = this.businessTableData.filter(
      business => !this.isBusinessDisabled(business)
    );
    const selectedAvailableBusinesses = availableBusinesses.filter(business =>
      this.selectedBusinesses.find(selected => selected.id === business.id)
    );

    if (availableBusinesses.length === 0) {
      this.selectAllChecked = false;
    } else {
      this.selectAllChecked =
        selectedAvailableBusinesses.length === availableBusinesses.length;
    }
  }

  /**
   * Check if all businesses on current page are disabled
   */
  areAllBusinessesDisabled(): boolean {
    return this.businessTableData.every(business =>
      this.isBusinessDisabled(business)
    );
  }

  onBusinessTablePageChange(event: any) {
    this.businessTableCurrentPage = event.page;
    this.businessTablePageSize = event.rows;
    this.updateBusinessTableData();
  }

  /**
   * Update business table data based on current page and filtered list
   */
  private updateBusinessTableData() {
    const startIndex =
      this.businessTableCurrentPage * this.businessTablePageSize;
    const endIndex =
      (this.businessTableCurrentPage + 1) * this.businessTablePageSize;

    this.businessTableData = this.filteredBusinessList.slice(
      startIndex,
      endIndex
    );
    this.updateSelectAllState();
  }

  isBusinessDisabled(business: BusinessItem): boolean {
    // Check if business has both execution and follow-up responsible people
    const hasResponsiblePeople =
      business.hasResponsiblePeople ||
      (business.hasResponsibleExecution && business.hasResponsibleFollowUp);

    // Check if business has the selected checklist (if a checklist is selected)
    const checklistId = this.activityInfoForm.get('checklistId')?.value;
    const hasChecklist = !checklistId || business.hasChecklist;

    return !hasResponsiblePeople || !hasChecklist;
  }

  getBusinessTooltip(business: BusinessItem): string {
    const hasResponsiblePeople =
      business.hasResponsiblePeople ||
      (business.hasResponsibleExecution && business.hasResponsibleFollowUp);

    if (!hasResponsiblePeople) {
      return this.translate.instant('NO_RESPONSIBLE_PEOPLE_SET');
    }

    const checklistId = this.activityInfoForm.get('checklistId')?.value;
    if (checklistId && !business.hasChecklist) {
      return this.translate.instant('CHECKLIST_NOT_ASSIGNED');
    }

    return '';
  }

  saveActivity() {
    if (this.activityInfoForm.valid && this.selectedBusinesses.length > 0) {
      // Show confirmation dialog
      const businessCount = this.selectedBusinesses.length;
      const message = this.translate
        .instant('ARE_YOU_SURE_CREATE_ACTIVITY_FOR_BUSINESSES')
        .replace('{count}', businessCount.toString());

      this.confirmationService.confirm({
        message: message,
        header: this.translate.instant('CONFIRMATION'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('CREATE'),
        rejectLabel: this.translate.instant('DISCARD'),
        accept: () => {
          this.performSave();
        }
      });
    } else {
      // Mark all form fields as touched to show validation errors
      this.markFormGroupTouched(this.activityInfoForm);
    }
  }

  private performSave() {
    const formData = new FormData();

    // Add activity information - use exact property names from ActivityPortalRequestDto
    formData.append('Title', this.activityInfoForm.get('title')?.value);
    formData.append(
      'Description',
      this.activityInfoForm.get('description')?.value || ''
    );

    const checklistId = this.activityInfoForm.get('checklistId')?.value;
    if (checklistId) {
      formData.append('ChecklistId', checklistId.toString());
    }

    // Add repetition ID
    const repetitionId = this.activityInfoForm.get('repetitionId')?.value;
    if (repetitionId) {
      formData.append('RepetitionId', repetitionId.toString());
    }

    // Add notification settings via ActivityScheduleDto
    const deadline = this.activityInfoForm.get('deadline')?.value;
    if (deadline) {
      // Convert Date to ISO string format for backend
      const year = deadline.getFullYear();
      const month = deadline.getMonth();
      const day = deadline.getDate();
      const utcMidnight = new Date(Date.UTC(year, month, day)); // 00:00:00Z for that date
      const isoDateString = utcMidnight.toISOString(); // "YYYY-MM-DDT00:00:00.000Z"

      formData.append('ActivityScheduleDto.StartDate', isoDateString);
      formData.append('ActivityScheduleDto.EndDate', isoDateString);
      formData.append(
        'ActivityScheduleDto.NotifyBeforeDeadLine',
        this.activityInfoForm.get('beforeDeadline')?.value?.toString() || '0'
      );
      formData.append(
        'ActivityScheduleDto.NotifyBeforeFollowup',
        this.activityInfoForm.get('afterDeadline')?.value?.toString() || '0'
      );
    }

    // Add selected businesses - try different formats for model binding
    const selectedBusinessIds = this.selectedBusinesses.map(b => b.id);

    // Try sending as simple array without indices
    selectedBusinessIds.forEach(businessId => {
      formData.append('BusinessIds', businessId.toString());
    });

    // Add files - send as multiple fields with same name for List<IFormFile>
    this.uploadedFiles.forEach(file => {
      formData.append('ActivityAttachment', file);
    });

    // Initialize progress loader configuration
    const totalActivitiesToCreate = this.selectedBusinesses.length;
    const initialEstimatedTime = totalActivitiesToCreate * 1; // one second for each activity
    
    this.progressLoaderConfig = {
      isVisible: true,
      title: this.translate.instant('CREATING_ACTIVITIES'),
      message: this.translate.instant('DONT_CLOSE_PAGE') + ' ' + this.translate.instant('ACTIVITIES_CREATION_MIGHT_TAKE'),
      progress: 0,
      totalItems: totalActivitiesToCreate,
      estimatedTimeRemaining: initialEstimatedTime,
      initialEstimatedTime: initialEstimatedTime,
      showProgressBar: true,
      showTimeRemaining: true,
      showItemCount: true
    };

    this.isSavingActivities = true;
    this.isLoading = true;

    // Call the batch API endpoint
    this.subscriptions.push(
      this.activityServices.createPortalActivityBatch(formData).subscribe({
        next: response => {
          this.isLoading = false;
          this.isSavingActivities = false;
          
          // Complete the progress bar smoothly
          this.completeProgressSmoothly();

          if (response?.success) {
            // Show success message
            this.notifications.success('UI_NOTIFICATIONS.SAVED');

            // Navigate back to activities list
            this.router.navigate(['../'], { relativeTo: this.route });
          } else {
            // Show error message
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
          }
        },
        error: error => {
          this.isLoading = false;
          this.isSavingActivities = false;
          this.progressLoaderConfig.isVisible = false;
          console.error('Error creating activities:', error);

          // Show error message
          
        }
      })
    );
  }

  /**
   * Complete the progress bar smoothly to 100% and then hide
   */
  completeProgressSmoothly(): void {
    if (this.progressLoader && this.progressLoader.completeProgress) {
      this.progressLoader.completeProgress();
    } else {
      // Fallback: just hide immediately if component reference is not available
      this.progressLoaderConfig.isVisible = false;
    }
  }

  goBack() {
    this.location.back();
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Handle file selection for upload
   * @param event {any}
   */
  onFileSelected(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        this.uploadedFiles.push(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.filePreviewUrls.push(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  }

  /**
   * Remove file from upload list
   * @param index {number}
   */
  removeFile(index: number) {
    this.uploadedFiles.splice(index, 1);
    this.filePreviewUrls.splice(index, 1);
  }

  /**
   * Get file icon path based on file type (matching edit-activity page)
   * @param file {File}
   * @returns {string}
   */
  getFileIcon(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return '/assets/file-icons/kb-file-pdf.png';
      case 'doc':
      case 'docx':
        return '/assets/file-icons/kb-file-docx.png';
      case 'xls':
      case 'xlsx':
        return '/assets/file-icons/kb-file-xls.png';
      case 'ppt':
      case 'pptx':
        return '/assets/file-icons/kb-file-ppt.png';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'tiff':
        return '/assets/file-icons/kb-file-image.png';
      case 'txt':
        return '/assets/icons/txt.png';
      case 'zip':
      case 'rar':
      case '7z':
        return '/assets/file-icons/kb-file-archive.png';
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case '3gpp':
      case 'webm':
      case 'flv':
        return '/assets/file-icons/kb-file-video.png';
      case 'mp3':
      case 'wav':
      case 'flac':
        return '/assets/file-icons/kb-file-audio.png';
      case 'csv':
      case 'rtf':
        return '/assets/file-icons/kb-file-general.png';
      default:
        return '/assets/file-icons/kb-file-unknown.png';
    }
  }

  /**
   * Apply search filter to business list and sort with available businesses first
   */
  private applyBusinessSearchFilter(
    businessList: BusinessItem[]
  ): BusinessItem[] {
    let filteredList = businessList;

    // Apply search filter if search term exists
    if (this.businessSearchTerm && this.businessSearchTerm.trim() !== '') {
      const searchTerm = this.businessSearchTerm.toLowerCase().trim();
      filteredList = businessList.filter(
        business =>
          business.name.toLowerCase().includes(searchTerm) ||
          business.companyName.toLowerCase().includes(searchTerm)
      );
    }

    // Sort: available businesses first (alphabetically), then unavailable (alphabetically)
    return filteredList.sort((a, b) => {
      const aAvailable = !this.isBusinessDisabled(a);
      const bAvailable = !this.isBusinessDisabled(b);

      // If availability is different, available comes first
      if (aAvailable !== bAvailable) {
        return aAvailable ? -1 : 1;
      }

      // If both have same availability, sort alphabetically
      const nameA = (a.name || a.companyName || '').toLowerCase();
      const nameB = (b.name || b.companyName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
